import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, setAccessToken } from '../lib/api';

/**
 * Auth state global (US-AUTH-02 + US-TR-10).
 *
 *  - `user` : profil minimal du user authentifié, ou null si déconnecté.
 *  - `loading` : true au démarrage tant qu'on n'a pas tenté un refresh
 *    pour restaurer la session depuis le cookie refresh httpOnly.
 *  - `login(email, password)` : POST /auth/login → stocke access token
 *    en mémoire + user dans le state.
 *  - `logout()` : POST /auth/logout + clear state.
 *  - `impersonateTenant(tenantId)` : super-admin uniquement (US-TR-10).
 *    POST /platform/tenants/:id/impersonate → switch d'access token vers
 *    une vue tenant, recharge `me`, redirige vers `/dashboard`.
 *  - `endImpersonation()` : POST /platform/end-impersonation → restaure le
 *    super-admin originel, recharge `me`, redirige vers `/platform/tenants`.
 *  - `isImpersonating` : true si le JWT courant porte un claim
 *    `impersonated_by` (le bandeau Header utilise cet état).
 *  - `impersonatedBy` : id du super-admin originel (depuis le JWT).
 */

export interface AuthTenant {
  id: string;
  code: string;
  nom: string;
  logo_url: string | null;
}

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  tenant_id: string | null;
  role_id: string | null;
  is_platform_admin: boolean;
  must_change_password: boolean;
  avatar_url: string | null;
  avatar_signed_url?: string | null;
  /** Tenant courant — null pour super-admin plateforme (US-DASH-03). */
  tenant: AuthTenant | null;
  /** Codes de permissions effectives (US-ADM-03). Super-admin = `['*']`. */
  permissions: string[];
  /** Noms des rôles effectifs (pour l'UI — ex. distinguer biomédical/prestataire). */
  role_names?: string[];
  /** True si la session courante est une impersonation (claim JWT). */
  is_impersonated?: boolean;
  /** Id du super-admin originel (claim JWT `impersonated_by`). */
  impersonated_by?: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login(email: string, password: string): Promise<AuthUser>;
  logout(): Promise<void>;
  impersonateTenant(tenantId: string): Promise<void>;
  endImpersonation(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Décode la partie payload d'un JWT sans vérifier la signature (la
 * vérification est faite côté backend). Utilisé uniquement pour lire les
 * claims non sensibles `impersonated_by` et exposer `is_impersonated` au UI.
 */
function decodeJwtClaims(token: string): { sub?: string; impersonated_by?: string | null } | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    // base64url → base64
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '==='.slice((normalized.length + 3) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function buildAuthUser(base: AuthUser, accessToken: string | null): AuthUser {
  const claims = accessToken ? decodeJwtClaims(accessToken) : null;
  const impersonatedBy = claims?.impersonated_by ?? null;
  return {
    ...base,
    impersonated_by: impersonatedBy,
    is_impersonated: Boolean(impersonatedBy),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Au montage : tente un refresh + récupération du user depuis /auth/me.
  // Permet de restaurer la session après un reload de page sans login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const refreshRes = await api.post<{ data: { access_token: string } }>('/auth/refresh', {});
        if (cancelled) return;
        const token = refreshRes.data.data.access_token;
        setAccessToken(token);
        const meRes = await api.get<{ data: AuthUser }>('/auth/me');
        if (cancelled) return;
        setUser(buildAuthUser(meRes.data.data, token));
      } catch {
        // Aucune session valide — utilisateur déconnecté.
        setAccessToken(null);
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Si l'intercepteur Axios diffuse un event auth:logout (refresh échoué),
  // on synchronise le state.
  useEffect(() => {
    function handleLogout() {
      setAccessToken(null);
      setUser(null);
    }
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<{
      data: { access_token: string; user: AuthUser };
    }>('/auth/login', { email, password });
    const token = res.data.data.access_token;
    setAccessToken(token);
    const next = buildAuthUser(res.data.data.user, token);
    setUser(next);
    return next;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const impersonateTenant = useCallback(async (tenantId: string) => {
    const res = await api.post<{
      data: { access_token: string; target_tenant: { id: string; code: string; nom: string } };
    }>(`/platform/tenants/${tenantId}/impersonate`);
    const token = res.data.data.access_token;
    setAccessToken(token);
    const meRes = await api.get<{ data: AuthUser }>('/auth/me');
    setUser(buildAuthUser(meRes.data.data, token));
    // NB : navigation déléguée à l'appelant (soft routing via useNavigate)
    // — un hard reload ré-authentifierait via le cookie refresh (toujours
    // associé au super-admin) et perdrait l'access token d'impersonation
    // (stocké en mémoire JS uniquement).
  }, []);

  const endImpersonation = useCallback(async () => {
    const res = await api.post<{ data: { access_token: string } }>('/platform/end-impersonation');
    const token = res.data.data.access_token;
    setAccessToken(token);
    const meRes = await api.get<{ data: AuthUser }>('/auth/me');
    setUser(buildAuthUser(meRes.data.data, token));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, logout, impersonateTenant, endImpersonation }),
    [user, loading, login, logout, impersonateTenant, endImpersonation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an <AuthProvider>');
  return ctx;
}
